import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useAdmins } from "../../../hooks/admins/useAdmins";
import {
  useCSVAdminData,
  CSVAdmin,
} from "../../../hooks/CSV/admins/useCSVAdminData";
import { useImportHistory } from "../../../hooks/CSV/admins/useImportHistoryAdmins";
import { importHistoryAdminAPI } from "../../../hooks/importHistory/importHistoryAdminAPI";
import { ImportHistoryTable } from "../list/CSV/ImportHistoryTable";
import { CSVAdminPreviewTable } from "../list/CSV/CSVAdminPreviewTable";
import { InvalidDataTable } from "../list/CSV/InvalidDataTable";
import ErrorDetailsModal from "../list/CSV/ErrorDetailsModal";
import { CSVAdminProcessingError } from "../../../utils/csv/csv-validatorAdmin";

type CSVAdminManagementViewProps = {
  token: string;
  onClose: () => void;
  refetch: () => void;
};

type ErrorDetail = {
  admin: CSVAdmin;
  error: string;
  is_restore?: boolean;
  restored?: boolean;
  id: string;
};

export function CSVAdminManagementView({
  token,
  onClose,
  refetch,
}: CSVAdminManagementViewProps) {
  const {
    selectedFile,
    setSelectedFile,
    CSVAdminError,
    CSVAdminPreviewData,
    invalidData,
    fileInputKey,
    setFileInputKey,
    validateAdmin,
    handleCSVAdminFileUpload,
    setCSVAdminPreviewData,
    setInvalidData,
  } = useCSVAdminData();

  const {
    importHistory,
    isFetchingHistory,
    fetchImportHistory,
    setImportHistory,
  } = useImportHistory(token);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<CSVAdmin | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetail[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  const {
    processCSVAdmin,
    isProcessingCSVAdmin,
    CSVAdminError: processCSVAdminError,
  } = useAdmins(token);

  useEffect(() => {
    fetchImportHistory();
  }, [fetchImportHistory]);

  const handleSaveImportError = useCallback(async () => {
    const errorImportStatusData = {
      file_name: selectedFile?.name || "Desconhecido",
      timestamp: new Date().toISOString(),
      status: "finished" as const,
      progress: 0,
      table_name: "admins",
      total: CSVAdminPreviewData.length,
      success: 0,
      detailed_errors: [] as {
        id: string;
        admin: CSVAdmin;
        error: string;
        is_restore?: boolean;
        restored?: boolean;
      }[],
      error: "Erro no processamento",
    };

    try {
      const errorImportStatus = await importHistoryAdminAPI.create(
        errorImportStatusData,
        token
      );
      setImportHistory((prev: any) => [errorImportStatus, ...prev]);
    } catch (saveError) {
      console.error(
        "Erro ao salvar histórico de importação com falha:",
        saveError
      );
    }
  }, [selectedFile, CSVAdminPreviewData.length, token, setImportHistory]);

  const handleProcessImportInBackground = useCallback(async () => {
    if (!selectedFile || CSVAdminPreviewData.length === 0) return;

    try {
      const result = await processCSVAdmin(CSVAdminPreviewData);

      const newImportStatusData = {
        file_name: selectedFile.name,
        timestamp: new Date().toISOString(),
        // Set status to 'finished'; errors are indicated by detailed_errors/error fields
        status: "finished" as const, // Ensure status matches expected type
        progress: result.success,
        total: CSVAdminPreviewData.length,
        table_name: "admins",
        success: result.success,
        detailed_errors: result.errors.map(
          (error: CSVAdminProcessingError) => ({
            id: error.id,
            admin: error.admin,
            error: error.error,
            is_restore: error.admin.is_restore,
            restored: false,
          })
        ),
        error:
          result.errors.length > 0
            ? `${result.errors.length} erro(s) ao importar`
            : undefined,
      };

      const newImportStatus = await importHistoryAdminAPI.create(
        newImportStatusData,
        token
      );
      setImportHistory((prev: any) => [newImportStatus, ...prev]);

      setSelectedFile(null);
      setCSVAdminPreviewData([]);
      setInvalidData([]);
      setFileInputKey(Date.now().toString()); 

      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        onClose();
        refetch();
      }, 3500);
    } catch (error) {
      console.error("Erro ao processar importação:", error);
      setProcessError("Erro ao processar importação.");
      handleSaveImportError();
    }
  }, [
    selectedFile,
    CSVAdminPreviewData,
    processCSVAdmin,
    token,
    refetch,
    handleSaveImportError,
    setImportHistory,
    setSelectedFile,
    setCSVAdminPreviewData,
    setInvalidData,
    setFileInputKey,
    onClose,
  ]);

  const handleAdminRestored = useCallback((id: string) => {
    setErrorDetails((prevErrorDetails) =>
      prevErrorDetails.map((errorDetail) =>
        errorDetail.id === id
          ? { ...errorDetail, restored: true, is_restore: false }
          : errorDetail
      )
    );
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  }, []);

  const handleRemovePreviewItem = useCallback(
    (index: number) => {
      setCSVAdminPreviewData((prev) => prev.filter((_, idx) => idx !== index));
    },
    [setCSVAdminPreviewData]
  );

  const handleRemoveInvalidItem = useCallback(
    (index: number) => {
      setInvalidData((prev) => prev.filter((_, idx) => idx !== index));
    },
    [setInvalidData]
  );

  const handleEditInvalidItem = useCallback(
    (index: number) => {
      setEditingIndex(index);
      setEditingAdmin({ ...invalidData[index] });
    },
    [invalidData]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingAdmin(null);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingAdmin !== null && editingIndex !== null) {
      const validatedAdmin = validateAdmin(editingAdmin);
      if (validatedAdmin.isValid) {
        setCSVAdminPreviewData((prev) => [...prev, validatedAdmin]);
        setInvalidData((prev) => prev.filter((_, idx) => idx !== editingIndex));
      } else {
        setInvalidData((prev) =>
          prev.map((item, idx) =>
            idx === editingIndex ? validatedAdmin : item
          )
        );
      }
      setEditingIndex(null);
      setEditingAdmin(null);
    }
  }, [
    editingAdmin,
    editingIndex,
    validateAdmin,
    setCSVAdminPreviewData,
    setInvalidData,
  ]);

  const handleChangeEditingAdmin = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setEditingAdmin((prev) => (prev ? { ...prev, [name]: value } : prev));
    },
    []
  );

  const openErrorModal = useCallback(
    (
      errors: {
        admin: CSVAdmin;
        error: string;
        is_restore?: boolean;
        id: string;
      }[]
    ) => {
      setErrorDetails((prevErrorDetails) => {
        const updatedErrorDetails: ErrorDetail[] = errors.map((error) => {
          const existingError = prevErrorDetails.find(
            (ed) => ed.id === error.id
          );
          return {
            ...error,
            restored: existingError ? existingError.restored : false,
          };
        });
        return updatedErrorDetails;
      });
      setShowErrorModal(true);
    },
    []
  );

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const downloadExampleCSV = () => {
    const exampleCSV =
      "Nome,E-mail\nExemplo1,Exemplo1@example.com\nExemplo2,Exemplo2@example.com";
    const blob = new Blob([exampleCSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-exemplo.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Modal show onHide={onClose} size="xl" dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Importar Administradores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImportHistoryTable
            importHistory={importHistory}
            isFetchingHistory={isFetchingHistory}
            openErrorModal={openErrorModal}
          />
          <Form.Group>
            <Form.Label>
              Arquivo .csv <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-2 align-items-center mb-2">
              <Button
                variant="outline-secondary"
                onClick={() => document.getElementById("csvFileInput")?.click()}
              >
                Escolher arquivo
              </Button>
              {selectedFile && (
                <span className="text-muted">{selectedFile.name}</span>
              )}
              <Form.Control
                type="file"
                accept=".csv"
                key={fileInputKey}
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  if (input.files && input.files[0]) {
                    handleCSVAdminFileUpload(input.files[0]);
                  }
                }}
                style={{ display: "none" }}
                id="csvFileInput"
                data-testid="csvFileInput" // Add data-testid
              />
            </div>
            <Form.Text className="text-muted">
              <strong>Padrão:</strong> O arquivo .csv não deve ter cabeçalho
              (nome das colunas). As colunas devem ser separadas por "," no
              seguinte formato:
              <br />
              Nome, E-mail
              <br />
              <Button variant="link" onClick={downloadExampleCSV}>
                Baixar modelo de exemplo
              </Button>
            </Form.Text>
          </Form.Group>

          {CSVAdminError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erros encontrados:</strong>
              <pre className="mb-0 mt-2 small">{CSVAdminError}</pre>
            </Alert>
          )}

          {processCSVAdminError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erro no processamento:</strong>
              <pre className="mb-0 mt-2 small">
                {processCSVAdminError.message}
              </pre>
            </Alert>
          )}

          <Form.Group className="mt-4">
            <Form.Label>Pesquisar administradores</Form.Label>
            <Form.Control
              type="text"
              placeholder="Pesquisar por nome ou email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>

          <CSVAdminPreviewTable
            data={CSVAdminPreviewData}
            searchTerm={searchTerm}
            handleRemovePreviewItem={handleRemovePreviewItem}
          />

          <InvalidDataTable
            data={invalidData}
            searchTerm={searchTerm}
            editingIndex={editingIndex}
            editingAdmin={editingAdmin}
            handleEditInvalidItem={handleEditInvalidItem}
            handleRemoveInvalidItem={handleRemoveInvalidItem}
            handleChangeEditingAdmin={handleChangeEditingAdmin}
            handleSaveEdit={handleSaveEdit}
            handleCancelEdit={handleCancelEdit}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleProcessImportInBackground}
            disabled={CSVAdminPreviewData.length === 0 || isProcessingCSVAdmin}
          >
            {isProcessingCSVAdmin ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                IMPORTANDO
              </>
            ) : (
              "IMPORTAR"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ErrorDetailsModal
        show={showErrorModal}
        onHide={closeErrorModal}
        errorDetails={errorDetails}
        token={token}
        onAdminRestored={handleAdminRestored}
      />

      {processError && (
        <Alert
          variant="danger"
          className="mt-3"
          onClose={() => setProcessError(null)}
          dismissible
        >
          {processError}
        </Alert>
      )}
    </>
  );
}

export default CSVAdminManagementView;
